// KPI strip (HANDOFF §1/§4): 2–4 cards, radius 16, label 12/600 slate, value
// 24/700 tabular + unit 12/600 mist. Wraps 2×2 on the 768–1023 viewport range
// (owner A) and inside any container ≤ 760 px (docked panel open); single
// column under 360 px of container width (phones). See components.css.
//
// RULE 7: `value` is a string the screen formatted from an API figure. Never
// pass a client-side sum. A comparison («+٣٪») only when the API returns it;
// a cell whose figure is unavailable is omitted, not zeroed.

import type { ReactNode } from 'react';
import { Skeleton } from './Skeleton';

export interface KpiCell {
  key: string;
  label: ReactNode;
  /** Formatted figure from the API, e.g. formatMoney(x, 'IQD', { unit: false }). */
  value: ReactNode;
  /** «د.ع», «بسعر ١٬٣٢٠», «٧١٪». */
  unit?: ReactNode;
  /** Optional comparison returned by the API, e.g. «+٣٪». */
  comparison?: ReactNode;
}

export interface KpiStripProps {
  cells: KpiCell[];
  /** Render shells with a shimmer value (page head + KPI shells render immediately). */
  loading?: boolean;
  /** How many shells to show while loading (default 4). */
  loadingCount?: number;
  'aria-label'?: string;
}

export function KpiStrip({ cells, loading, loadingCount = 4, 'aria-label': ariaLabel }: KpiStripProps) {
  if (loading) {
    return (
      <div className="e-kpis cq" aria-busy="true" aria-label={ariaLabel}>
        {Array.from({ length: loadingCount }, (_, i) => (
          <div key={i} className="e-kpi" data-skeleton="true">
            <Skeleton width="45%" height={12} />
            <Skeleton width="70%" height={24} />
          </div>
        ))}
      </div>
    );
  }
  if (cells.length === 0) return null;
  return (
    <div className="e-kpis cq" aria-label={ariaLabel} data-count={cells.length}>
      {cells.map((c) => (
        <div key={c.key} className="e-kpi">
          <span className="e-kpi-label">{c.label}</span>
          <span className="e-kpi-value">
            {c.value}
            {c.unit && <span className="e-kpi-unit">{c.unit}</span>}
          </span>
          {c.comparison && <span className="e-kpi-cmp">{c.comparison}</span>}
        </div>
      ))}
    </div>
  );
}
